import { Pie } from '@ant-design/plots';
import { checkRateFixedPointStr, priceToString } from '../../util/utils';
import {
  Col,
  Descriptions,
  Row,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import Title from 'antd/lib/typography/Title';
import styled from 'styled-components';
import { styledTable } from '../styled/mixin';
import { VendorType } from '../../modules/vendors';
import { InvoiceStatus } from '../../modules/invoices';

interface ExpenseReportComponentProps {
  visualData1: any[];
  visualData2: any;
  visualData3: any[];
  visualData4: any[];
  visualData6: any;
  downloadContractReport: () => void;
}

const StyledDiv = styled.div`
  ${styledTable}
`;

function ExpenseReportComponent({
  visualData1,
  visualData2,
  visualData3,
  visualData4,
  visualData6,
  downloadContractReport,
}: ExpenseReportComponentProps) {
  const configVisualData1 = {
    appendPadding: 10,
    data: visualData1,
    angleField: 'value',
    colorField: 'type',
    radius: 1,
    innerRadius: 0.6,
    label: {
      type: 'inner',
      offset: '-50%',
      content: ({ percent }: any) => `${(percent * 100).toFixed(0)}%`,
      style: {
        textAlign: 'center',
        fontSize: 14,
      },
    },
    tooltip: {
      formatter: (datum: any) => {
        return { name: datum.type, value: `${priceToString(datum.value)}` };
      },
    },
    interactions: [
      {
        type: 'element-selected',
      },
      {
        type: 'element-active',
      },
    ],
    statistic: {
      // title: false,
      // title: undefined,
      // title: '합계',
      content: {
        style: {
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        content: `${priceToString(
          visualData1.reduce((prev, next) => prev + next.value, 0),
        )}`,
        // content: 'asdf',
      },
    },
  };

  const configVisualData3 = {
    appendPadding: 10,
    data: visualData3,
    angleField: 'value',
    colorField: 'type',
    radius: 1,
    innerRadius: 0.6,
    label: {
      type: 'inner',
      offset: '-50%',
      content: ({ percent }: any) => `${(percent * 100).toFixed(0)}%`,
      style: {
        textAlign: 'center',
        fontSize: 14,
      },
    },
    tooltip: {
      formatter: (datum: any) => {
        return { name: datum.type, value: `${priceToString(datum.value)}` };
      },
    },
    interactions: [
      {
        type: 'element-selected',
      },
      {
        type: 'element-active',
      },
    ],
    statistic: {
      // title: false,
      // title: undefined,
      // title: '합계',
      content: {
        style: {
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        content: `${priceToString(
          visualData1.reduce((prev, next) => prev + next.value, 0),
        )}`,
        // content: 'asdf',
      },
    },
  };

  const columnsVisualData1: ColumnsType<any> = [
    {
      title: '용도',
      dataIndex: 'type',
      align: 'center',
    },
    {
      title: '금액(원)',
      dataIndex: 'value',
      defaultSortOrder: 'descend',
      align: 'right',
      sorter: (a: any, b: any) => a.value - b.value,
      render: (_, record: any) =>
        record.value ? priceToString(record.value) : null,
    },
  ];

  const columnsVisualData3: ColumnsType<any> = [
    {
      title: '공정',
      dataIndex: 'type',
      align: 'center',
    },
    {
      title: '금액(원)',
      dataIndex: 'value',
      defaultSortOrder: 'descend',
      align: 'right',
      sorter: (a: any, b: any) => a.value - b.value,
      render: (_, record: any) =>
        record.value ? priceToString(record.value) : null,
    },
  ];

  const columnsVisualData6: ColumnsType<any> = [
    {
      title: '공정',
      dataIndex: 'gongjong',
      sorter: (a: any, b: any) => a.gongjong.localeCompare(b.gongjong),
      defaultSortOrder: 'ascend',
      align: 'center',
    },
    {
      title: '계약금액(A)',
      align: 'right',
      render: (_, record: any) => {
        let price = 0;
        if (record.data === undefined) return;
        record.data
          .filter((d: any) => d.type === 'P')
          .forEach((d: any) => {
            price += d.price;
          });
        return priceToString(price);
      },
    },
    {
      title: '설계변경금액(B)',
      align: 'right',
      render: (_, record: any) => {
        let price = 0;
        if (record.data === undefined) return;
        record.data
          .filter((d: any) => d.type === 'C')
          .forEach((d: any) => {
            price += d.price;
          });
        // return priceToString(price);
        return (
          <Tooltip
            placement="topLeft"
            title={
              <ul>
                {record.data
                  .filter((d: any) => d.type === 'C')
                  .map((item: any, index: any) => (
                    <li key={index}>{item.note}</li>
                  ))}
              </ul>
            }
          >
            {priceToString(price)}
          </Tooltip>
        );
      },
    },
    {
      title: '총계약금액(C=A+B)',
      align: 'right',
      render: (_, record: any) => {
        let price = 0;
        if (record.data === undefined) return;
        record.data
          .filter((d: any) => d.type === 'P' || d.type === 'C')
          .forEach((d: any) => {
            price += d.price;
          });
        return priceToString(price);
      },
    },
    {
      title: '청구금액',
      align: 'center',
      children: [
        {
          title: '승인중(D)',
          align: 'right',
          render: (_, record: any) => {
            let price = 0;
            if (record.data === undefined) return;
            record.data
              .filter((d: any) => d.type === 'I')
              .filter((d: any) => d.status === InvoiceStatus.APPROVING)
              .forEach((d: any) => {
                price += d.price;
              });
            // return priceToString(price);
            return (
              <Tooltip
                placement="topLeft"
                overlayInnerStyle={{ width: '500px' }}
                title={
                  <ul>
                    {record.data
                      .filter((d: any) => d.type === 'I')
                      .filter((d: any) => d.status === InvoiceStatus.APPROVING)
                      .map((item: any, index: any) => (
                        <li key={index}>{item.note}</li>
                      ))}
                  </ul>
                }
              >
                {priceToString(price)}
              </Tooltip>
            );
          },
        },
        {
          title: '승인완료(E)',
          align: 'right',
          render: (_, record: any) => {
            let price = 0;
            if (record.data === undefined) return;
            record.data
              .filter((d: any) => d.type === 'I')
              .filter((d: any) => d.status === InvoiceStatus.APPROVED)
              .forEach((d: any) => {
                price += d.price;
              });
            // return priceToString(price);
            return (
              <Tooltip
                placement="topRight"
                overlayInnerStyle={{ width: '500px' }}
                title={
                  <ul>
                    {record.data
                      .filter((d: any) => d.type === 'I')
                      .filter((d: any) => d.status === InvoiceStatus.APPROVED)
                      .map((item: any, index: any) => (
                        <li key={index}>{item.note}</li>
                      ))}
                  </ul>
                }
              >
                {priceToString(price)}
              </Tooltip>
            );
          },
        },
        {
          title: '지급완료(F)',
          align: 'right',
          render: (_, record: any) => {
            let price = 0;
            if (record.data === undefined) return;
            record.data
              .filter((d: any) => d.type === 'I')
              .filter((d: any) => d.status === InvoiceStatus.PAYMENT_COMPLETED)
              .forEach((d: any) => {
                price += d.price;
              });
            // return priceToString(price);
            return (
              <Tooltip
                placement="topRight"
                overlayInnerStyle={{ width: '500px' }}
                title={
                  <ul>
                    {record.data
                      .filter((d: any) => d.type === 'I')
                      .filter(
                        (d: any) =>
                          d.status === InvoiceStatus.PAYMENT_COMPLETED,
                      )
                      .map((item: any, index: any) => (
                        <li key={index}>{item.note}</li>
                      ))}
                  </ul>
                }
              >
                {priceToString(price)}
              </Tooltip>
            );
          },
        },
        {
          title: '합계(G=E+F)',
          align: 'right',
          render: (_, record: any) => {
            let price = 0;
            if (record.data === undefined) return;
            record.data
              .filter((d: any) => d.type === 'I')
              .filter(
                (d: any) =>
                  d.status === InvoiceStatus.APPROVED ||
                  d.status === InvoiceStatus.PAYMENT_COMPLETED,
              )
              .forEach((d: any) => {
                price += d.price;
              });
            return priceToString(price);
          },
        },
      ],
    },
    {
      title: `${
        visualData6.isInvoiceEnabled ? '지출금액(H)' : '지출/실행금액(D)'
      }`,
      align: 'right',
      render: (_, record: any) => {
        let price = 0;
        if (record.data === undefined) return;
        record.data
          .filter((d: any) => d.type === 'E')
          .forEach((d: any) => {
            price += d.price;
          });
        // return priceToString(price);
        return (
          <Tooltip
            placement="topRight"
            overlayInnerStyle={{ width: '500px' }}
            title={
              <ul>
                {record.data
                  .filter((d: any) => d.type === 'E')
                  .map((item: any, index: any) => (
                    <li key={index}>{item.note}</li>
                  ))}
              </ul>
            }
          >
            {priceToString(price)}
          </Tooltip>
        );
      },
    },
    {
      title: '실행금액(I=G+H)',
      align: 'right',
      render: (_, record: any) => {
        let price = 0;
        if (record.data === undefined) return;
        record.data
          .filter((d: any) => d.type === 'E')
          .forEach((d: any) => {
            price += d.price;
          });
        record.data
          .filter((d: any) => d.type === 'I')
          .filter(
            (d: any) =>
              d.status === InvoiceStatus.APPROVED ||
              d.status === InvoiceStatus.PAYMENT_COMPLETED,
          )
          .forEach((d: any) => {
            price += d.price;
          });
        return priceToString(price);
      },
    },
  ];

  if (!visualData6.isInvoiceEnabled) {
    columnsVisualData6.splice(4, 1);
    columnsVisualData6.splice(5, 1);
  }

  // const columnsVisualData4: ColumnsType<any> = [
  //   {
  //     title: '거래처명',
  //     dataIndex: 'name',
  //     sorter: (a: any, b: any) => a.name.localeCompare(b.name),
  //     ellipsis: true,
  //     align: 'center',
  //   },
  //   {
  //     title: '공종',
  //     dataIndex: 'gongjong',
  //     align: 'center',
  //   },
  //   {
  //     title: '승인금액(원)',
  //     dataIndex: 'value',
  //     align: 'right',
  //     sorter: (a: any, b: any) => a.value - b.value,
  //     render: (_, record: any) =>
  //       record.value ? priceToString(record.value) : null,
  //   },
  //   {
  //     title: '연면적당 금액(원)',
  //     dataIndex: 'valuePerArea',
  //     defaultSortOrder: 'descend',
  //     align: 'right',
  //     render: (_, record: any) =>
  //       record.valuePerArea ? priceToString(record.valuePerArea) : null,
  //   },
  // ];

  const columnsVisualData5: ColumnsType<any> = [
    {
      title: '거래처명',
      dataIndex: 'name',
      align: 'center',
    },
    {
      title: '공종',
      dataIndex: 'gongjong',
      align: 'center',
    },
    {
      title: '합계금액(원)',
      align: 'right',
      render: (_, record: any) =>
        record.price ? priceToString(record.price) : null,
    },
    {
      title: '지급금액(원)',
      align: 'right',
      render: (_, record: any) => priceToString(record.paymentAmount),
    },
    {
      title: '지급율',
      defaultSortOrder: 'descend',
      align: 'right',
      render: (_, record: any) =>
        checkRateFixedPointStr(record.paymentAmount / record.price),
    },
    {
      title: '미지급금액(원)',
      align: 'right',
      render: (_, record: any) =>
        priceToString(record.price - record.paymentAmount),
    },
    {
      title: '미지급율',
      defaultSortOrder: 'descend',
      align: 'right',
      render: (_, record: any) =>
        checkRateFixedPointStr(1 - record.paymentAmount / record.price),
    },
  ];

  // console.log(visualData1);
  // console.log(visualData2);
  // console.log(visualData3);
  return (
    <StyledDiv>
      <Row style={{ marginTop: '10px' }}>
        <Col>
          <Space align="baseline">
            <Title level={4}>정산현황 개요</Title>
            <Typography.Text>
              (공급가액/승인금액 기준, 단위: 원)
            </Typography.Text>
          </Space>
        </Col>
      </Row>
      <Descriptions
        bordered
        layout="vertical"
        column={7}
        size="small"
        // style={{ marginBottom: '50px' }}
      >
        <Descriptions.Item
          label="계약금액(A)"
          labelStyle={{
            display: 'flex',
            justifyContent: 'end',
          }}
          contentStyle={{ display: 'flex', justifyContent: 'end' }}
        >
          {priceToString(visualData2.contractPrice)}
        </Descriptions.Item>
        <Descriptions.Item
          label="설계변경금액(B)"
          labelStyle={{ display: 'flex', justifyContent: 'end' }}
          contentStyle={{ display: 'flex', justifyContent: 'end' }}
        >
          {priceToString(visualData2.changeOrderPrice)}
        </Descriptions.Item>
        <Descriptions.Item
          label="총계약금액(C=A+B)"
          labelStyle={{ display: 'flex', justifyContent: 'end' }}
          contentStyle={{ display: 'flex', justifyContent: 'end' }}
        >
          {priceToString(visualData2.totalContractPrice)}
        </Descriptions.Item>
        <Descriptions.Item
          label="실행금액(D)"
          labelStyle={{ display: 'flex', justifyContent: 'end' }}
          contentStyle={{ display: 'flex', justifyContent: 'end' }}
        >
          {priceToString(visualData2.totalExpensePrice)}
        </Descriptions.Item>
        <Descriptions.Item
          label="실행율(E=D/C)"
          labelStyle={{ display: 'flex', justifyContent: 'center' }}
          contentStyle={{ display: 'flex', justifyContent: 'center' }}
        >
          {checkRateFixedPointStr(
            visualData2.totalExpensePrice / visualData2.totalContractPrice,
          )}
        </Descriptions.Item>
        {/* <Descriptions.Item label="이윤(F=C-D)">
          {priceToString(visualData2.profit)} (
          {checkRateFixedPointStr(
            1 - visualData2.totalExpensePrice / visualData2.contractPrice,
          )}
          )
        </Descriptions.Item> */}
        <Descriptions.Item
          label="이윤(F=C-D)"
          labelStyle={{ display: 'flex', justifyContent: 'end' }}
          contentStyle={{ display: 'flex', justifyContent: 'end' }}
        >
          {priceToString(visualData2.profit)}
        </Descriptions.Item>
        <Descriptions.Item
          label="이윤율(G=100-E)"
          labelStyle={{ display: 'flex', justifyContent: 'center' }}
          contentStyle={{ display: 'flex', justifyContent: 'center' }}
        >
          {checkRateFixedPointStr(
            1 - visualData2.totalExpensePrice / visualData2.totalContractPrice,
          )}
        </Descriptions.Item>
      </Descriptions>
      {visualData6 && visualData6.items && visualData6.items.length > 0 && (
        <>
          <Row style={{ marginTop: '50px' }}>
            <Col>
              <Space align="baseline">
                <Title level={4}>정산현황 상세</Title>
                <Typography.Text>
                  (공급가액/승인금액 기준, 단위: 원)
                </Typography.Text>
              </Space>
            </Col>
          </Row>
          <Row justify="space-between">
            <Col span={24}>
              <Table
                columns={columnsVisualData6}
                dataSource={visualData6.items}
                pagination={false}
                bordered
                summary={(pageData) => {
                  let pValue = 0;
                  let cValue = 0;
                  let iValue1 = 0;
                  let iValue2 = 0;
                  let iValue3 = 0;
                  let eValue = 0;
                  pageData.forEach(({ data }) => {
                    data.forEach((item: any) => {
                      if (item.type === 'P') pValue += item.price;
                      if (item.type === 'C') cValue += item.price;
                      if (
                        item.type === 'I' &&
                        item.status === InvoiceStatus.APPROVING
                      )
                        iValue1 += item.price;
                      if (
                        item.type === 'I' &&
                        item.status === InvoiceStatus.APPROVED
                      )
                        iValue2 += item.price;
                      if (
                        item.type === 'I' &&
                        item.status === InvoiceStatus.PAYMENT_COMPLETED
                      )
                        iValue3 += item.price;
                      if (item.type === 'E') eValue += item.price;
                    });
                  });
                  return (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} align="center">
                          Total:
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          {priceToString(pValue)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                          {priceToString(cValue)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          {priceToString(pValue + cValue)}
                        </Table.Summary.Cell>
                        {visualData6.isInvoiceEnabled && (
                          <>
                            <Table.Summary.Cell index={4} align="right">
                              {priceToString(iValue1)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={5} align="right">
                              {priceToString(iValue2)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={6} align="right">
                              {priceToString(iValue3)}
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={7} align="right">
                              {priceToString(iValue2 + iValue3)}
                            </Table.Summary.Cell>
                          </>
                        )}
                        <Table.Summary.Cell index={8} align="right">
                          {priceToString(eValue)}
                        </Table.Summary.Cell>
                        {visualData6.isInvoiceEnabled && (
                          <Table.Summary.Cell index={9} align="right">
                            {priceToString(iValue2 + iValue3 + eValue)}
                          </Table.Summary.Cell>
                        )}
                      </Table.Summary.Row>
                    </>
                  );
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Typography.Text type="secondary">
                * 미분류: 공정 분류가 되지 않은 금액
              </Typography.Text>
            </Col>
          </Row>
          <Row>
            <Col>
              <Typography.Text type="secondary">
                * 기타금액: 도급계약,설계변경에 포함된 금액 중 직접공사비 이외의
                금액
              </Typography.Text>
            </Col>
          </Row>
        </>
      )}
      <Row style={{ marginTop: '50px' }}>
        <Col>
          <Title level={4}>공정별 지출현황</Title>
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span={12}>
          <Pie {...configVisualData3} />
        </Col>
        <Col span={8}>
          <Table
            columns={columnsVisualData3}
            dataSource={visualData3}
            pagination={false}
            bordered
            summary={(pageData) => {
              let totalValue = 0;
              pageData.forEach(({ value }) => {
                totalValue += value;
              });
              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} align="center">
                      Total:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      {priceToString(totalValue)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        </Col>
      </Row>
      <Row style={{ marginTop: '50px' }}>
        <Col>
          <Title level={4}>용도별 지출현황</Title>
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span={12}>
          <Pie {...configVisualData1} />
        </Col>
        <Col span={8}>
          <Table
            columns={columnsVisualData1}
            dataSource={visualData1}
            pagination={false}
            bordered
            summary={(pageData) => {
              let totalValue = 0;

              pageData.forEach(({ value }) => {
                totalValue += value;
              });

              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} align="center">
                      Total:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      {priceToString(totalValue)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        </Col>
      </Row>
      {/* <Row style={{ marginTop: '50px' }}>
        <Col>
          <Space align="baseline">
            <Title level={4}>사업자별 실적현황</Title>
            <Typography.Text>(공급가액 기준)</Typography.Text>
          </Space>
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span={22}>
          <Table
            columns={columnsVisualData4}
            dataSource={visualData4.filter(
              (data) => data.type === VendorType.COMPANY,
            )}
            pagination={false}
            bordered
            summary={(pageData) => {
              let totalValue = 0;

              pageData.forEach(({ value }) => {
                totalValue += value;
              });

              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="center">
                      Total:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      {priceToString(totalValue)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        </Col>
      </Row>

      <Row style={{ marginTop: '50px' }}>
        <Col>
          <Space align="baseline">
            <Title level={4}>작업자별 실적현황</Title>
            <Typography.Text>(공급가액 기준)</Typography.Text>
          </Space>
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span={22}>
          <Table
            columns={columnsVisualData4}
            dataSource={visualData4.filter(
              (data) => data.type === VendorType.WORKER,
            )}
            pagination={false}
            bordered
            summary={(pageData) => {
              let totalValue = 0;

              pageData.forEach(({ value }) => {
                totalValue += value;
              });

              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="center">
                      Total:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      {priceToString(totalValue)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        </Col>
      </Row> */}

      <Row style={{ marginTop: '50px' }}>
        <Col>
          <Space align="baseline">
            <Title level={4}>사업자별 지급/미지급 현황</Title>
            <Typography.Text>(부가세포함금액 기준)</Typography.Text>
          </Space>
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span={22}>
          <Table
            columns={columnsVisualData5}
            dataSource={visualData4.filter(
              (data) => data.type === VendorType.COMPANY,
            )}
            pagination={false}
            bordered
            summary={(pageData) => {
              let totalPrice = 0;
              let totalPaymentAmount = 0;

              pageData.forEach(({ price, paymentAmount }) => {
                totalPrice += price;
                totalPaymentAmount += paymentAmount;
              });

              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="center">
                      Total:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      {priceToString(totalPrice)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      {priceToString(totalPaymentAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}></Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {priceToString(totalPrice - totalPaymentAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        </Col>
      </Row>

      <Row style={{ marginTop: '50px' }}>
        <Col>
          <Space align="baseline">
            <Title level={4}>작업자별 지급/미지급 현황</Title>
            <Typography.Text>(부가세포함금액 기준)</Typography.Text>
          </Space>
        </Col>
      </Row>
      <Row justify="space-between">
        <Col span={22}>
          <Table
            columns={columnsVisualData5}
            dataSource={visualData4.filter(
              (data) => data.type === VendorType.WORKER,
            )}
            pagination={false}
            bordered
            summary={(pageData) => {
              let totalPrice = 0;
              let totalPaymentAmount = 0;

              pageData.forEach(({ price, paymentAmount }) => {
                totalPrice += price;
                totalPaymentAmount += paymentAmount;
              });

              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="center">
                      Total:
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      {priceToString(totalPrice)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      {priceToString(totalPaymentAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}></Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {priceToString(totalPrice - totalPaymentAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6}></Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        </Col>
      </Row>

      {/* <Row style={{ marginTop: '50px' }}>
        <Col>
          <Title level={4}>보고서 다운로드</Title>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button onClick={downloadContractReport}>공사계약현황</Button>
        </Col>
      </Row> */}
    </StyledDiv>
  );
}

export default ExpenseReportComponent;
